import {
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import type { CSSProperties } from 'react';

import type { TNode, TreeData } from '@revelio/ui';
import { searchMatch, TreeSelection } from '@revelio/ui';

import { CircularBadge } from '../../CircularBadge';
import { EntityInfoPopover } from '../../EntityInfoPopover';
import { VariableLengthSkeleton } from '../../variable-length-skeleton/variable-length-skeleton';
import styles from './SplitResultsTree.module.css';
import layoutStyles from './split-results-tree.module.css';
import { useSplitTree } from './useSplitTree';

const TAB_PANEL_PADDING = 8;
const BUTTON_HEIGHT = 24;
const BUTTON_MARGIN = 2;
const CONTENT_HEIGHT_OFFSET = TAB_PANEL_PADDING + BUTTON_HEIGHT + BUTTON_MARGIN;

interface SplitResultsTreeProps {
  companyResults: TreeData[];
  industryResults: TreeData[];
  onCompanyChange: (ids: string[], nodes?: TNode[]) => void;
  onIndustryChange: (ids: string[], nodes?: TNode[]) => void;
  height?: number;
  isLoading?: boolean;
  value?: string[];
  search?: string;
  activeTab?: number;
  onTabChange?: (index: number) => void;
  /** Render only the flat company list, without the Company/Industry tabs. */
  companyOnly?: boolean;
  sortSelectedIds?: string[];
}

function TreeSkeleton({ height }: { height: number }) {
  return (
    <div style={{ height }}>
      <VariableLengthSkeleton
        count={8}
        fade
        checkboxes
        className={layoutStyles.loadingSkeleton}
        data-testid="tree-loading-skeleton"
      />
    </div>
  );
}

export const SplitResultsTree = ({
  companyResults,
  industryResults,
  onCompanyChange,
  onIndustryChange,
  height = 400,
  isLoading = false,
  value = [],
  search = '',
  activeTab = 0,
  onTabChange,
  companyOnly = false,
  sortSelectedIds,
}: SplitResultsTreeProps) => {
  const { companyTreeApi, industryTreeApi, companyCount, industryCount } =
    useSplitTree({
      companyResults,
      industryResults,
      value,
      search,
      onCompanyChange,
      onIndustryChange,
    });

  const adjustedHeight = height - TAB_PANEL_PADDING;
  const contentHeight = adjustedHeight - CONTENT_HEIGHT_OFFSET;

  const handleIndustryExpand = () => {
    if (industryTreeApi.isExpanded) {
      // biome-ignore lint/suspicious/useIterableCallbackReturn: migrated from develop
      industryTreeApi.treeRef.current?.root?.children?.forEach((node) =>
        node.close()
      );
      industryTreeApi.collapse();
    } else {
      // biome-ignore lint/suspicious/useIterableCallbackReturn: migrated from develop
      industryTreeApi.treeRef.current?.root?.children?.forEach((node) =>
        node.open()
      );
      industryTreeApi.expand();
    }
  };

  if (companyOnly) {
    return isLoading ? (
      <TreeSkeleton height={height} />
    ) : (
      <TreeSelection
        data={companyResults}
        treeApi={companyTreeApi}
        sortSelectedIds={sortSelectedIds}
        height={height}
        searchMatch={(node, search) => {
          if (search.length < 2) {
            return searchMatch(node, search);
          }
          return true;
        }}
        drillMode={{
          isEnabled: true,
          level: 0,
          isRoot: true,
          drillIntoNode: () => null,
          drillOutToLevel: () => null,
          isDrilledInto: () => false,
        }}
        popoverComponent={EntityInfoPopover}
      />
    );
  }

  return (
    <Tabs
      variant="line"
      colorScheme="green"
      size="sm"
      height="100%"
      index={activeTab}
      onChange={onTabChange}
    >
      <TabList display="flex" mb={1}>
        <Tab
          flex="1"
          textAlign="center"
          _selected={{
            opacity: 1,
            color: 'green.500',
            borderColor: 'green.500',
          }}
          opacity={0.6}
          role="tab"
          aria-label="Company"
          className={styles.tab}
        >
          <Text fontSize="xs">Company</Text>{' '}
          {companyCount > 0 && (
            <div
              className={layoutStyles.splitTreeCompanyTabBadge}
              data-testid="split-tree-company-tab-badge"
            >
              <CircularBadge value={companyCount} colorScheme="navyBlue" />
            </div>
          )}
        </Tab>
        <Tab
          flex="1"
          textAlign="center"
          _selected={{
            opacity: 1,
            color: 'green.500',
            borderColor: 'green.500',
          }}
          opacity={0.6}
          role="tab"
          aria-label="Industry"
          className={styles.tab}
        >
          <Text fontSize="xs">Industry</Text>
          {industryCount > 0 && (
            <div
              className={layoutStyles.splitTreeCompanyTabBadge}
              data-testid="split-tree-industry-tab-badge"
            >
              <CircularBadge value={industryCount} colorScheme="navyBlue" />
            </div>
          )}
        </Tab>
      </TabList>

      <TabPanels>
        <TabPanel p={0}>
          {isLoading ? (
            <TreeSkeleton height={adjustedHeight} />
          ) : (
            <TreeSelection
              data={companyResults}
              treeApi={companyTreeApi}
              sortSelectedIds={sortSelectedIds}
              height={adjustedHeight}
              searchMatch={(node, search) => {
                if (search.length < 2) {
                  return searchMatch(node, search);
                }
                return true;
              }}
              drillMode={{
                isEnabled: true,
                level: 0,
                isRoot: true,
                drillIntoNode: () => null,
                drillOutToLevel: () => null,
                isDrilledInto: () => false,
              }}
              popoverComponent={EntityInfoPopover}
            />
          )}
        </TabPanel>
        <TabPanel p={0}>
          <div
            className={layoutStyles.industryPanel}
            style={
              {
                '--industry-panel-height': `${adjustedHeight}px`,
              } as CSSProperties
            }
          >
            {!isLoading && (
              <button
                type="button"
                className={styles.subtleLinkButton}
                onClick={handleIndustryExpand}
              >
                {industryTreeApi.isExpanded ? 'Collapse All' : 'Expand All'}
              </button>
            )}
            {isLoading ? (
              <TreeSkeleton height={contentHeight} />
            ) : (
              <TreeSelection
                data={industryResults}
                treeApi={industryTreeApi}
                sortSelectedIds={sortSelectedIds}
                height={contentHeight}
                openByDefault={false}
                popoverComponent={EntityInfoPopover}
              />
            )}
          </div>
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};
